import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/SubscriptionContainer.module.scss';
import SubscriptionAppBar from '../../components/SubscriptionAppBar';
import CurrentSubscription from '../components/CurrentSubscription';
import ManageSubscriptionButtons from './components/ManageSubscriptionButtons';
import { useDispatch } from 'react-redux';
import { petContextActions } from '../../../../reducers/series3UpgradePetContext';
import { inAppCancellation as events } from '../../../../lib/analytics/events';
import { usePurchaseSubscriptionContext } from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import { purchaseSubscriptionDeviceDetails } from '../../../../types/gql-op-types';
import { getSubscriptionCategory } from '../util/helpers';

/**
 * @returns true if this device has redeemed an upgrade and awaiting activation of their new device
 */
function isDeviceAwaitingUpgradeActivation(device: purchaseSubscriptionDeviceDetails): boolean {
  if (!device.upgradeRedeemed) {
    return false;
  }

  // purchaseableSubscriptionOptions will only have subscription plans that match the device series.
  // When the customer redeems an upgrade for this device, we assign the new subscription to their
  // old device while they wait for their new device to arrive. So if the user is awaiting upgrade activation,
  // the current subscription for this device will be on a plan not typically available for purchase on this device.
  const purchasableSubscriptionSkus = device.purchasableSubscriptionOptions.map((option) => option.sku);
  const currentSubscriptionSku = device.subscription?.subscriptionOption?.sku;
  const hasUpgradedSubscription =
    !!currentSubscriptionSku && !purchasableSubscriptionSkus.includes(currentSubscriptionSku);

  return hasUpgradedSubscription;
}

export default function SubscriptionSplash() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  dispatch(petContextActions.setInAppCancellationFlow(true));
  const { device } = usePurchaseSubscriptionContext();
  const currentSubscriptionCategory = getSubscriptionCategory(device.subscription?.subscriptionOption?.renewalMonths);

  // If a device has been upgraded, don't let the user make any further modifications to their
  // subscription. Without this, users can get into a state where they have upgraded to S3, but
  // then modify their old S2 subscription and downgrade their subscription back to S2.
  const awaitingUpgradeActivation = isDeviceAwaitingUpgradeActivation(device);

  // If device is in a renewable state, user can renew and we show canceled design states.
  const canRenew = !!device.subscriptionRenewalStatus?.shouldOfferSubscriptionRenewal;

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          title={t('membership')}
          backButtonExitsWebview={true}
          backButtonAnalyticsEvent={() => events.manageMembershipBack({ petId: device.pet?.id })}
          closeButtonAnalyticsEvent={() => events.manageMembershipClose({ petId: device.pet?.id })}
        />
        {awaitingUpgradeActivation ? (
          <>
            <div className={styles.subscriptionContainer}>
              <p>
                {t(
                  'this_subscription_has_been_upgraded_to_series_3_plus_you_cannot_make_further_modifications_at_this_time_once_you_receive_and_activate_your_series_3_plus_collar_you_will_be_able_to_manage_your_membership',
                )}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className={styles.subscriptionContainer}>
              <h1>{t('your_current_plan')}</h1>
              <CurrentSubscription canRenew={canRenew} />
              <ManageSubscriptionButtons canRenew={canRenew} subscriptionCategory={currentSubscriptionCategory} />
            </div>
          </>
        )}
      </div>
    </>
  );
}
